import React, { useState, useEffect } from 'react';
import {
  User,
  Mail,
  Save,
  CheckCircle2,
  AlertCircle,
  CreditCard,
  Zap,
  Check,
  ShieldCheck,
  Database,
  MessageSquare
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';

const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    document.title = 'manteo.ai | Profile';
  }, []);

  useEffect(() => {
    if (user) {
      setFullName(user.full_name || '');
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage(null);
    try {
      await updateUser({ full_name: fullName });
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (err) {
      console.error('Failed to update profile:', err);
      setMessage({ type: 'error', text: 'Failed to update profile. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50/50 dark:bg-gray-950 px-8 py-12 transition-colors duration-300">
      <div className="max-w-4xl mx-auto">
        <header className="mb-12">
          <h1 className="text-3xl font-black text-gray-900 dark:text-white uppercase tracking-tight">Profile</h1>
          <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">Manage your account details</p>
        </header>

        <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-[32px] overflow-hidden shadow-sm">
          <form onSubmit={handleSubmit} className="p-8">
            <div className="space-y-6">
              {/* Full Name Field */}
              <div>
                <label className="block text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">
                  Full Name
                </label>
                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-emerald-500 transition-colors">
                    <User size={18} />
                  </div>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800 rounded-2xl text-sm font-bold text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all placeholder:text-gray-400"
                    placeholder="Enter your full name"
                    required
                  />
                </div>
              </div>

              {/* Email Field (Read-only) */}
              <div>
                <label className="block text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">
                  Email Address
                </label>
                <div className="relative group opacity-60">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                    <Mail size={18} />
                  </div>
                  <input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="w-full pl-12 pr-4 py-3.5 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl text-sm font-bold text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  />
                </div>
                <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mt-2 px-1 italic">
                  Email address cannot be changed
                </p>
              </div>
            </div>

            {message && (
              <div className={cn(
                "mt-8 p-4 rounded-2xl flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-200",
                message.type === 'success' ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 border border-emerald-100 dark:border-emerald-800/50" : "bg-red-50 dark:bg-red-900/20 text-red-600 border border-red-100 dark:border-red-800/50"
              )}>
                {message.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
                <p className="text-xs font-bold uppercase tracking-tight">{message.message || message.text}</p>
              </div>
            )}

            <div className="mt-10 pt-8 border-t border-gray-50 dark:border-gray-800 flex justify-end">
              <button
                type="submit"
                disabled={isSaving}
                className="flex items-center gap-2 px-8 py-3 bg-emerald-600 text-white rounded-2xl hover:bg-emerald-700 transition-all font-black text-[10px] uppercase tracking-widest shadow-lg shadow-emerald-600/10 active:scale-95 disabled:opacity-50 disabled:active:scale-100"
              >
                {isSaving ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Save size={16} />
                )}
                Save Changes
              </button>
            </div>
          </form>
        </div>

        <section className="mt-10">
          <h2 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tight mb-6">Billing & Subscription</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2 bg-white dark:bg-gray-900 border border-emerald-500/20 dark:border-emerald-500/10 rounded-[32px] p-1 shadow-xl shadow-emerald-500/5 relative overflow-hidden group">
              <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl group-hover:bg-emerald-500/20 transition-colors" />

              <div className="relative bg-white dark:bg-gray-900 rounded-[30px] p-8 border border-emerald-50 dark:border-emerald-900/30">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-8">
                  <div>
                    <span className="px-3 py-1 bg-emerald-500 text-white text-[9px] font-black uppercase tracking-[0.2em] rounded-lg shadow-lg shadow-emerald-500/20">
                      Active Plan
                    </span>
                    <h3 className="text-3xl font-black text-gray-900 dark:text-white uppercase tracking-tighter mt-3">Pilot Plan</h3>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-1 flex items-center gap-2">
                      <ShieldCheck size={14} className="text-emerald-500" />
                      Early Adopter Benefits Enabled
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-4xl font-black text-gray-900 dark:text-white">$0</span>
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-2">/ month</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  <div className="p-5 bg-gray-50 dark:bg-gray-800/50 rounded-3xl border border-gray-100 dark:border-gray-800">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 rounded-xl">
                        <MessageSquare size={18} />
                      </div>
                      <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none">Usage</span>
                    </div>
                    <p className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-tight">Unlimited</p>
                    <p className="text-[9px] text-gray-500 font-bold uppercase tracking-widest mt-1">Chat messages</p>
                  </div>

                  <div className="p-5 bg-gray-50 dark:bg-gray-800/50 rounded-3xl border border-gray-100 dark:border-gray-800">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-xl">
                        <Database size={18} />
                      </div>
                      <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none">Capacity</span>
                    </div>
                    <p className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-tight">Unlimited</p>
                    <p className="text-[9px] text-gray-500 font-bold uppercase tracking-widest mt-1">Warehouse storage</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">Included Features</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3">
                    {[
                      'Advanced AI Agents',
                      'BigQuery Integration',
                      'Custom Map Visualizations',
                      'Multi-workspace Support',
                      'Dedicated Support Channel',
                      'AI-powered Analytics'
                    ].map((feature) => (
                      <div key={feature} className="flex items-center gap-2 group/feat">
                        <div className="w-5 h-5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center text-emerald-500 group-hover/feat:scale-110 transition-transform">
                          <Check size={12} strokeWidth={4} />
                        </div>
                        <span className="text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-tight">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-3xl p-6">
                <div className="flex items-center gap-3 mb-5">
                  <CreditCard size={20} className="text-gray-400" />
                  <h3 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tight">Payment Method</h3>
                </div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-relaxed">
                  No payment method required during the pilot phase. Enjoy full platform access.
                </p>
              </div>

              <div className="bg-emerald-600 rounded-3xl p-6 text-white shadow-lg shadow-emerald-600/20">
                <div className="flex items-center gap-3 mb-4">
                  <Zap size={20} />
                  <h3 className="text-sm font-black uppercase tracking-tight">Priority Access</h3>
                </div>
                <p className="text-[10px] font-bold text-emerald-50 leading-relaxed uppercase tracking-widest mb-6">
                  As a pilot user, you'll be among the first to try the new features.
                </p>
                <div className="h-1 w-full bg-white/20 rounded-full overflow-hidden">
                  <div className="h-full bg-white w-full" />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ProfilePage;
